import React from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { MobileUnsupported } from './components/MobileUnsupported';
import { MaintenancePage } from './pages/MaintenancePage';
import { SubscriptionDashboard } from './pages/SubscriptionDashboard';
import { SubscriptionDetailPage } from './pages/SubscriptionDetailPage';
import { WineClubDashboard } from './pages/WineClubDashboard';
import { OrdersPage } from './pages/OrdersPage';
import { TagManagementPage } from './pages/TagManagementPage';
import { SettingsGeneralPage } from './pages/SettingsGeneralPage';
import { SettingsOrderProcessingPage } from './pages/SettingsOrderProcessingPage';
import { SettingsCustomerPortalPage } from './pages/SettingsCustomerPortalPage';
import { SettingsAccountBillingPage } from './pages/SettingsAccountBillingPage';
import { CancellationFlowPage } from './pages/CancellationFlowPage';
import { CancellationSettingsPage } from './pages/CancellationSettingsPage';
import { BoxBuilderPage } from './pages/BoxBuilderPage';
import { ContactsPage } from './pages/ContactsPage';
import { ContactDetailPage } from './pages/ContactDetailPage';
import { CohortRetentionPage } from './pages/CohortRetentionPage';
import { ProductsPage } from './pages/ProductsPage';
import { AIChatPage } from './pages/AIChatPage';
import { PaymentRecoveryPage } from './pages/PaymentRecoveryPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ProductReplacePage } from './pages/ProductReplacePage';
import { OperationsActivityLogPage } from './pages/OperationsActivityLogPage';
import { TasksPage } from './pages/TasksPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { ReportsPage } from './pages/ReportsPage';
import { MonthlyClubReportPage } from './pages/MonthlyClubReportPage';
import { LoyaltyOverviewPage } from './pages/LoyaltyOverviewPage';
import { LoyaltySetupWizardPage } from './pages/LoyaltySetupWizardPage';
import { LoyaltyMembersPage } from './pages/LoyaltyMembersPage';
import { LoyaltyRewardsPage } from './pages/LoyaltyRewardsPage';
import { LoyaltyRulesPage } from './pages/LoyaltyRulesPage';
import { AnniversaryRewardsPage } from './pages/AnniversaryRewardsPage';
import { DigitalWalletPage } from './pages/DigitalWalletPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AllocationsPage } from './pages/AllocationsPage';
import { CreateAllocationPage } from './pages/CreateAllocationPage';
import { AllocationDetailPage } from './pages/AllocationDetailPage';
import { AllocationTemplatesPage } from './pages/AllocationTemplatesPage';
import { MigrationHubPage } from './pages/MigrationHubPage';
import { MigrationWizardPage } from './pages/MigrationWizardPage';
import { MigrationDetailPage } from './pages/MigrationDetailPage';
import { AutomationRulesPage } from './pages/AutomationRulesPage';
import { CreateAutomationRulePage } from './pages/CreateAutomationRulePage';
import { useAppMode } from './contexts/AppModeContext';
// Catch-all for any unmatched route. Without this, an unknown path renders a
// blank content area (no route matches). Keeps stray links from dead-ending.
function NotFoundRoute() {
  const navigate = useNavigate();
  return <div className="bg-[#F1F5F9] min-h-full p-6 flex items-center justify-center" data-id="element-8" data-mp="k6yr4851d6cs">
      <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm w-full max-w-[460px] p-10 text-center" data-id="element-9" data-mp="f6ubekbycfnm">
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2" data-id="element-10" data-mp="ag42wy88bwop">
          Page not found
        </h2>
        <p className="text-sm text-[#64748B] mb-6" data-id="element-11" data-mp="6ftzntbtnkdj">
          That page doesn't exist or has moved.
        </p>
        <button onClick={() => navigate('/')} className="h-9 px-4 bg-[#0EA5E9] text-white rounded-md text-sm font-medium hover:bg-[#0284C7] transition-colors" data-id="element-12" data-mp="1gz5jv8em9e4">
          Back to Dashboard
        </button>
      </div>
    </div>;
}
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isLoyaltyOnly
  } = useAppMode();
  // Determine current page from route
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return isLoyaltyOnly ? 'Overview' : 'Dashboard';
    if (path === '/dashboard/retention') return 'Cohort Retention';
    if (path === '/subscriptions') return 'Subscriptions';
    if (path === '/analytics') return 'Analytics';
    if (path === '/orders') return 'Orders';
    if (path === '/allocations') return 'All Allocations';
    if (path === '/allocations/templates') return 'Templates';
    if (path === '/ai-chat') return 'AI Chat';
    if (path === '/club-builder') return 'Club Builder';
    if (path === '/products') return 'Products';
    if (path === '/contacts') return 'Contacts';
    if (path.startsWith('/contacts/')) return 'Contacts';
    if (path === '/integrations') return 'Integrations';
    if (path === '/loyalty/members') return 'Members';
    if (path === '/loyalty/rewards') return 'Rewards';
    if (path === '/loyalty/rules') return 'Rules & Tiers';
    if (path === '/loyalty/anniversary') return 'Anniversary Rewards';
    if (path === '/loyalty/wallet') return 'Digital Wallet';
    if (path === '/loyalty') return 'Overview';
    if (path === '/settings' || path === '/settings/general') return 'General';
    if (path === '/settings/tags') return 'Member Tags';
    if (path === '/settings/automation-rules/create') return 'Automation Rules';
    if (path.startsWith('/settings/automation-rules/')) return 'Automation Rules';
    if (path === '/settings/automation-rules') return 'Automation Rules';
    if (path === '/settings/order-processing') return 'Order Processing';
    if (path === '/settings/customer-portal') return 'Customer Portal';
    if (path === '/settings/account-billing') return 'Account & Billing';
    if (path === '/management/cancellation-flow') return 'Cancellation Flow';
    if (path === '/management/tools/cancellation-settings') return 'Cancellation Tools';
    if (path === '/management/payment-recovery') return 'Payment Recovery';
    if (path === '/management/notifications') return 'Notifications';
    if (path === '/management/migration') return 'Migration';
    if (path === '/management/migration/new') return 'Migration';
    if (path.startsWith('/management/migration/')) return 'Migration';
    if (path === '/operations/product-replace') return 'Product Replace';
    if (path === '/operations/activity-log') return 'Activity Log';
    if (path === '/operations/tasks') return 'Tasks';
    if (path.startsWith('/operations/reports') || path.startsWith('/reports')) return 'Reports';
    // Check for subscription detail route
    if (path.startsWith('/subscriptions/')) return 'Subscriptions';
    return 'Dashboard';
  };
  // Handle navigation from Sidebar
  const handleNavigate = (page: string) => {
    const routeMap: Record<string, string> = {
      Dashboard: '/',
      'Cohort Retention': '/dashboard/retention',
      Analytics: '/analytics',
      Subscriptions: '/subscriptions',
      Orders: '/orders',
      Contacts: '/contacts',
      Contact: '/contacts',
      'AI Chat': '/ai-chat',
      Chat: '/ai-chat',
      'Club Builder': '/club-builder',
      Products: '/products',
      Integrations: '/integrations',
      'Loyalty & Rewards': '/loyalty',
      Overview: '/loyalty',
      Members: '/loyalty/members',
      Rewards: '/loyalty/rewards',
      'Rules & Tiers': '/loyalty/rules',
      'Anniversary Rewards': '/loyalty/anniversary',
      'Digital Wallet': '/loyalty/wallet',
      'All Allocations': '/allocations',
      Templates: '/allocations/templates',
      Settings: '/settings/general',
      General: '/settings/general',
      'Order Processing': '/settings/order-processing',
      'Customer Portal': '/settings/customer-portal',
      'Account & Billing': '/settings/account-billing',
      'Account Settings': '/settings/account-billing',
      'Tag Management': '/settings/tags',
      Tags: '/settings/tags',
      'Member Tags': '/settings/tags',
      'Automation Rules': '/settings/automation-rules',
      'Cancellation Flow': '/management/cancellation-flow',
      'Cancellation Tools': '/management/tools/cancellation-settings',
      'Cancellation Settings': '/management/tools/cancellation-settings',
      'Payment Recovery': '/management/payment-recovery',
      Notifications: '/management/notifications',
      Migration: '/management/migration',
      'Product Replace': '/operations/product-replace',
      'Activity Log': '/operations/activity-log',
      'Operations Activity Log': '/operations/activity-log',
      Tasks: '/operations/tasks',
      Reports: '/operations/reports'
    };
    const route = routeMap[page] || '/';
    navigate(route);
  };
  const currentPage = getCurrentPage();
  return <>
      {/* Mobile Unsupported Overlay - Shows on mobile, hides everything else */}
      <MobileUnsupported data-id="element-13" data-mp="3ffgzy78dhtg5" />

      {/* Main App Layout - Hidden on mobile, visible on tablet and desktop */}
      <div className="hidden md:flex h-screen w-full bg-[#F1F5F9]" data-id="element-14" data-mp="1xlro2ikv9myj">
        {/* Sidebar - only visible on md and up */}
        <Sidebar currentPage={currentPage} onNavigate={handleNavigate} data-id="element-15" data-mp="26kd1y0fe8dsz" />

        {/* Page content */}
        <div className="flex-1 overflow-auto min-h-screen" data-id="element-16" data-mp="22jb49o14k9t4">
          <Routes data-id="element-17" data-mp="22gx8te2j8vl1">
            {/* Maintenance state - renders as a full-screen overlay (fixed inset-0) that covers the sidebar */}
            <Route path="/maintenance" element={<MaintenancePage data-id="element-19" data-mp="19vnkucblbp82" />} data-id="element-18" data-mp="19umaka2ef6re" />
            <Route path="/" element={isLoyaltyOnly ? <LoyaltyOverviewPage data-id="element-21" data-mp="1nmah7xtvp30z" /> : <WineClubDashboard data-id="element-22" data-mp="1nmkabua7zqaw" />} data-id="element-20" data-mp="19s9kf9mhlq39" />
            <Route path="/dashboard/retention" element={<CohortRetentionPage data-id="element-24" data-mp="1iusgnavrp4gj" />} data-id="element-23" data-mp="1nnloi7mj8swu" />

            <Route path="/analytics" element={<AnalyticsPage data-id="element-26" data-mp="v57z8wf9if9d" />} data-id="element-25" data-mp="1itr7jbybas1g" />
            <Route path="/subscriptions" element={<SubscriptionDashboard data-id="element-28" data-mp="v3dcrdocef36" />} data-id="element-27" data-mp="v4epdml0b9n0" />
            <Route path="/subscriptions/:id" element={<SubscriptionDetailPage data-id="element-30" data-mp="r3cx5ppibxuj" />} data-id="element-29" data-mp="r4ecw7hgx8il" />

            <Route path="/orders" element={<OrdersPage data-id="element-32" data-mp="m0jnlf88p3m3" />} data-id="element-31" data-mp="m1cmxv4vcqu7" />
            <Route path="/products" element={<ProductsPage data-id="element-34" data-mp="h2i3qs2zp09k" />} data-id="element-33" data-mp="m0tfj8cuhkuf" />
            <Route path="/contacts" element={<ContactsPage data-id="element-36" data-mp="h3b3h93j6pi7" />} data-id="element-35" data-mp="h29owunb3kvq" />
            <Route path="/contacts/:id" element={<ContactDetailPage data-id="element-38" data-mp="czvacmqo77a9" />} data-id="element-37" data-mp="d0wjglo4ljpc" />
            <Route path="/ai-chat" element={<AIChatPage data-id="element-40" data-mp="823i1rm45j5j" />} data-id="element-39" data-mp="d30zg1zau95z" />
            <Route path="/club-builder" element={<BoxBuilderPage data-id="element-42" data-mp="83xsud3o7uvw" />} data-id="element-41" data-mp="80s44yyyev0p" />
            <Route path="/integrations" element={<IntegrationsPage data-id="element-44" data-mp="33ii7tarx1hm" />} data-id="element-43" data-mp="32gv2qghgojj" />
            <Route path="/loyalty" element={<LoyaltyOverviewPage data-id="element-46" data-mp="3ugwcaj2f5p3z" />} data-id="element-45" data-mp="34thbft9xl2e" />
            <Route path="/loyalty/setup" element={<LoyaltySetupWizardPage data-id="element-48" data-mp="3uflb8r0cyvgk" />} data-id="element-47" data-mp="3ufv0gcdkwyl7" />
            <Route path="/loyalty/members" element={<LoyaltyMembersPage data-id="element-50" data-mp="3pg8nm41nxkcx" />} data-id="element-49" data-mp="3pjczlnlddke5" />
            <Route path="/loyalty/rewards" element={<LoyaltyRewardsPage data-id="element-52" data-mp="3liaxmz02iu8h" />} data-id="element-51" data-mp="3lh9lssb8a3pp" />
            <Route path="/loyalty/rules" element={<LoyaltyRulesPage data-id="element-54" data-mp="3ghwqdj6ajobi" />} data-id="element-53" data-mp="3ghn86hrhmlgd" />
            <Route path="/loyalty/anniversary" element={<AnniversaryRewardsPage data-id="element-56" data-mp="3bitpmaxcg49c" />} data-id="element-55" data-mp="3ggva90af4bmx" />
            <Route path="/loyalty/wallet" element={<DigitalWalletPage data-id="element-58" data-mp="1r0hx8xq1rwif" />} data-id="element-57" data-mp="3bj27kh8bpzre" />
            <Route path="/allocations/create" element={<CreateAllocationPage data-id="element-60" data-mp="1r2lz7a4dylg2" />} data-id="element-59" data-mp="1r1axrgyexps4" />

            <Route path="/allocations/:id/edit" element={<CreateAllocationPage data-id="element-62" data-mp="1vy1co0yhefby" />} data-id="element-61" data-mp="1vz2wouxh6c5t" />

            <Route path="/allocations/templates" element={<AllocationTemplatesPage data-id="element-64" data-mp="2100dp46r105q" />} data-id="element-63" data-mp="20yyq608jql74" />

            <Route path="/allocations/:id" element={<AllocationDetailPage data-id="element-66" data-mp="24z9727xvmw0r" />} data-id="element-65" data-mp="250aiwempvmjj" />
            <Route path="/allocations" element={<AllocationsPage data-id="element-68" data-mp="18a9uwhu69a3a" />} data-id="element-67" data-mp="24y6gy28doxlw" />
            <Route path="/settings" element={<SettingsGeneralPage data-id="element-70" data-mp="187x9tvt4ajm0" />} data-id="element-69" data-mp="18bb8asynu8o6" />
            <Route path="/settings/general" element={<SettingsGeneralPage data-id="element-72" data-mp="1d9mmtgzwh7cq" />} data-id="element-71" data-mp="1d8l54todpmm1" />
            <Route path="/settings/order-processing" element={<SettingsOrderProcessingPage data-id="element-74" data-mp="1h9wrmqbyhrq0" />} data-id="element-73" data-mp="1hapwtmv9o961" />

            <Route path="/settings/customer-portal" element={<SettingsCustomerPortalPage data-id="element-76" data-mp="1m280iioocnup" />} data-id="element-75" data-mp="1m1etdglszwc1" />

            <Route path="/settings/account-billing" element={<SettingsAccountBillingPage data-id="element-78" data-mp="piqy4sqqqs9p" />} data-id="element-77" data-mp="pjifozni6pgz" />

            <Route path="/settings/tags" element={<TagManagementPage data-id="element-80" data-mp="tk2d84bznv3n" />} data-id="element-79" data-mp="tl42bcpuai4d" />
            <Route path="/settings/automation-rules/create" element={<CreateAutomationRulePage data-id="element-82" data-mp="1wv9g1vihwc77" />} data-id="element-81" data-mp="tjaptgsjp3og" />

            <Route path="/settings/automation-rules/:id" element={<CreateAutomationRulePage data-id="element-84" data-mp="1rxq26wppm1fs" />} data-id="element-83" data-mp="1wtydfz0sdaho" />

            <Route path="/settings/automation-rules" element={<AutomationRulesPage data-id="element-86" data-mp="26m44n6j7rnvt" />} data-id="element-85" data-mp="1ruu67dxf9mtz" />

            <Route path="/management/cancellation-flow" element={<CancellationFlowPage data-id="element-88" data-mp="21m81f9icc0hb" />} data-id="element-87" data-mp="26l1dqylw5lfw" />

            <Route path="/management/tools/cancellation-settings" element={<CancellationSettingsPage data-id="element-90" data-mp="1dwdnspq767w0" />} data-id="element-89" data-mp="21n9ne90wa7dt" />

            <Route path="/management/payment-recovery" element={<PaymentRecoveryPage data-id="element-92" data-mp="18ym067wl57xm" />} data-id="element-91" data-mp="1dxfbbtoegmum" />

            <Route path="/management/notifications" element={<NotificationsPage data-id="element-94" data-mp="1mxlikxti8qlc" />} data-id="element-93" data-mp="18zncefpc80gx" />

            <Route path="/management/migration" element={<MigrationHubPage data-id="element-96" data-mp="1iw9rsoyw9zbk" />} data-id="element-95" data-mp="1mymx5c9qbv7t" />

            <Route path="/management/migration/new" element={<MigrationWizardPage data-id="element-98" data-mp="1iycpzwa7ayrd" />} data-id="element-97" data-mp="1iv72uml4u6ya" />

            <Route path="/management/migration/:id" element={<MigrationDetailPage data-id="element-100" data-mp="2yb7cewab7gd7" />} data-id="element-99" data-mp="2yc8x7sh4nh84" />

            <Route path="/operations/product-replace" element={<ProductReplacePage data-id="element-102" data-mp="2ub5hd5z2xl6w" />} data-id="element-101" data-mp="2ubyfxjnvx4dy" />

            <Route path="/operations/activity-log" element={<OperationsActivityLogPage data-id="element-104" data-mp="31u4wgzficqkg" />} data-id="element-103" data-mp="31v7pbcwe5330" />

            <Route path="/operations/tasks" element={<TasksPage data-id="element-106" data-mp="36v2dv9sp1gkw" />} data-id="element-105" data-mp="36u15jd32b86v" />
            <Route path="/operations/reports" element={<ReportsPage data-id="element-108" data-mp="2rxa2mtu6jeci" />} data-id="element-107" data-mp="36vc9bcwicfxz" />
            <Route path="/operations/reports/monthly-club-report" element={<MonthlyClubReportPage data-id="element-110" data-mp="2wvlbb18qnit5" />} data-id="element-109" data-mp="2ry1ym5rove3b" />
            <Route path="/reports/monthly-club-report" element={<MonthlyClubReportPage data-id="element-112" data-mp="3klfrnss9npi5" />} data-id="element-111" data-mp="2wwmphel1wlf3" />
            <Route path="*" element={<NotFoundRoute data-id="element-114" data-mp="3km7ikqax4z23" />} data-id="element-113" data-mp="3kmqr5geoib3g" />
          </Routes>
        </div>
      </div>
    </>;
}
export function AppRouter() {
  return <BrowserRouter data-id="element-115" data-mp="3pmd3zoqlhpbq">
      <AppContent data-id="element-116" data-mp="3avw7mjsthplp" />
    </BrowserRouter>;
}